import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Alert,
} from '@mui/material';
import { Inventory, Warning } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { consumeMaterial, MaterialRequirement } from '../../../store/slices/operatorSlice';

const MaterialConsumption: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { materialRequirements, activeOperation } = useSelector(
    (state: RootState) => state.operator
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialRequirement | null>(null);
  const [consumeQty, setConsumeQty] = useState(0);
  const [lotNumber, setLotNumber] = useState('');
  const [qtyError, setQtyError] = useState('');

  const handleOpenConsume = (material: MaterialRequirement) => {
    setSelectedMaterial(material);
    const remaining = material.requiredQty - material.consumedQty;
    setConsumeQty(remaining > 0 ? remaining : 0);
    setLotNumber(material.lotNumber || '');
    setQtyError('');
    setDialogOpen(true);
  };

  const handleConsume = () => {
    if (!selectedMaterial) return;
    if (consumeQty <= 0) {
      setQtyError('Quantity must be greater than 0');
      return;
    }
    dispatch(
      consumeMaterial({
        id: selectedMaterial.id,
        qty: consumeQty,
        lotNumber: lotNumber.trim() || undefined,
      })
    );
    setDialogOpen(false);
    setSelectedMaterial(null);
  };

  const getConsumptionColor = (material: MaterialRequirement) => {
    const pct = material.requiredQty > 0 ? (material.consumedQty / material.requiredQty) * 100 : 0;
    if (pct >= 100) return 'success';
    if (material.isShort) return 'error';
    if (pct > 50) return 'warning';
    return 'info';
  };

  if (!activeOperation) {
    return (
      <Alert severity="info" icon={<Inventory />}>
        Select a work order to view material requirements.
      </Alert>
    );
  }

  const shortages = materialRequirements.filter((m) => m.isShort);

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Material Requirements
          </Typography>
          <Chip
            label={`${materialRequirements.length} items`}
            size="small"
            variant="outlined"
          />
        </Box>

        {shortages.length > 0 && (
          <Alert severity="error" icon={<Warning />} sx={{ mb: 2 }}>
            Material shortage detected: {shortages.map((s) => s.itemName).join(', ')}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item Code</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell align="right">Required</TableCell>
                <TableCell align="right">Consumed</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Lot</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {materialRequirements.map((material) => {
                const pct =
                  material.requiredQty > 0
                    ? Math.min((material.consumedQty / material.requiredQty) * 100, 100)
                    : 0;
                const isFullyConsumed = material.consumedQty >= material.requiredQty;

                return (
                  <TableRow
                    key={material.id}
                    sx={{ bgcolor: material.isShort ? 'error.50' : 'inherit' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {material.itemCode}
                      </Typography>
                    </TableCell>
                    <TableCell>{material.itemName}</TableCell>
                    <TableCell align="right">
                      {material.requiredQty} {material.uom}
                    </TableCell>
                    <TableCell align="right">
                      {material.consumedQty} {material.uom}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          color={getConsumptionColor(material)}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ minWidth: 32 }}>
                          {pct.toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {material.lotNumber || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          isFullyConsumed
                            ? 'DONE'
                            : material.isShort
                            ? 'SHORT'
                            : 'PARTIAL'
                        }
                        color={
                          isFullyConsumed
                            ? 'success'
                            : material.isShort
                            ? 'error'
                            : 'warning'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenConsume(material)}
                        disabled={isFullyConsumed}
                      >
                        Consume
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Consume Material Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Consume Material</DialogTitle>
          <DialogContent>
            {selectedMaterial && (
              <Box>
                <Typography variant="body1" fontWeight="medium" gutterBottom>
                  {selectedMaterial.itemName}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {selectedMaterial.itemCode}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                  Required: {selectedMaterial.requiredQty} {selectedMaterial.uom} |
                  Already consumed: {selectedMaterial.consumedQty} {selectedMaterial.uom} |
                  Remaining: {selectedMaterial.requiredQty - selectedMaterial.consumedQty} {selectedMaterial.uom}
                </Typography>
                <TextField
                  label={`Quantity to Consume (${selectedMaterial.uom})`}
                  type="number"
                  value={consumeQty}
                  onChange={(e) => {
                    setConsumeQty(Number(e.target.value));
                    setQtyError('');
                  }}
                  fullWidth
                  margin="normal"
                  error={!!qtyError}
                  helperText={qtyError}
                  inputProps={{ min: 0, max: selectedMaterial.requiredQty - selectedMaterial.consumedQty }}
                />
                <TextField
                  label="Lot / Batch Number (optional)"
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  fullWidth
                  margin="normal"
                  placeholder="e.g. LOT-2024-001"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConsume} variant="contained" color="primary">
              Record Consumption
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default MaterialConsumption;
